import { JSX, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";

type LimitOption = { value: number | string; label: string };

type MetaFilter = {
  key: string;
  label?: string;
  default?: number | string;
  options?: LimitOption[];
};

// Deviations arrive precomputed by the API. Each measure has its own
// reference (see metadata definitions):
//   median_deviation_days = median_tat_days - average_payer_median_tat_days
//   mean_deviation_days   = mean_tat_days   - overall_mean_tat_days
type DeviationPoint = {
  name: string;
  median_tat_days?: number | null;
  mean_tat_days?: number | null;
  median_deviation_days?: number | null;
  mean_deviation_days?: number | null;
  insufficient_data?: boolean;
  decided?: number;
};

type Metadata = {
  top_n?: number | string;
  total_payers?: number;
  payers_returned?: number;
  // Reference for median deviations: mean of included payer medians.
  average_payer_median_tat_days?: number | null;
  // Reference for mean deviations: mean of included payer means
  // (older responses sent overall_mean_tat_days instead).
  average_payer_mean_tat_days?: number | null;
  overall_mean_tat_days?: number | null;
  fence_stage?: string;
  filters?: MetaFilter[];
  [key: string]: unknown;
};

type DeviationBarData = {
  data?: DeviationPoint[];
  metadata?: Metadata;
  // Siblings of .data, not nested under .metadata.
  has_download_options?: boolean;
  download_options?: WidgetDownloadOptions;
};

type DeviationBarWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
  // deviation_bar self-fetches (see `canFetch` below), so WidgetRenderer's
  // own query for this widget is disabled and it never sees
  // has_download_options to decide whether its header hamburger button
  // should render. This reports the value back up once known — same pattern
  // as CustomTableWidget.
  onDownloadOptionsChange?: (hasDownloadOptions: boolean) => void;
};

type Measure = "median" | "mean";

const MEASURES: Array<{ key: Measure; label: string }> = [
  { key: "median", label: "Median" },
  { key: "mean", label: "Mean" },
];

const FALLBACK_LIMIT_OPTIONS: LimitOption[] = [
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
  { value: "all", label: "All" },
];

const isNumeric = (val: unknown): val is number =>
  typeof val === "number" && !isNaN(val);

const fmtDays = (val: number | null): string =>
  val === null ? "–" : `${val.toFixed(1)}d`;

const unwrap = (raw: unknown): DeviationBarData => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj as DeviationBarData;
  return obj as DeviationBarData;
};

export default function DeviationBarWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
  onDownloadOptionsChange,
}: DeviationBarWidgetProps): JSX.Element {
  const [limit, setLimit] = useState<number | string | null>(null);
  // Which reference the bars deviate from. Median is the default (robust to
  // outliers); Mean is offered so a median↔mean gap exposes skewed payers.
  const [measure, setMeasure] = useState<Measure>("median");

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading: isFetching } = useQuery({
    queryKey: [
      "widget",
      dashboardName,
      widgetTemplateId,
      appliedFilters,
      limit,
    ],
    queryFn: ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName!,
        String(widgetTemplateId!),
        {
          filters: appliedFilters,
          widget_filters: limit === null ? undefined : { top_n: limit },
        },
        { signal },
      ),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const parsed = useMemo(
    () => unwrap(canFetch ? queryData : data),
    [canFetch, queryData, data],
  );
  const meta = parsed.metadata;
  const points = useMemo(() => parsed.data ?? [], [parsed]);

  useEffect(() => {
    onDownloadOptionsChange?.(!!parsed.has_download_options);
  }, [parsed.has_download_options, onDownloadOptionsChange]);

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={
        parsed.has_download_options ? parsed.download_options : undefined
      }
    />
  );

  const overall: Record<Measure, number | null> = {
    median: isNumeric(meta?.average_payer_median_tat_days)
      ? meta.average_payer_median_tat_days
      : null,
    mean: isNumeric(meta?.average_payer_mean_tat_days)
      ? meta.average_payer_mean_tat_days
      : isNumeric(meta?.overall_mean_tat_days)
        ? meta.overall_mean_tat_days
        : null,
  };
  const measureLabel = measure === "median" ? "Median" : "Mean";

  const deviationOf = (pt: DeviationPoint): number | null => {
    if (pt.insufficient_data) return null;
    const v =
      measure === "median" ? pt.median_deviation_days : pt.mean_deviation_days;
    return isNumeric(v) ? v : null;
  };

  const topNFilter = meta?.filters?.find((f) => f.key === "top_n");
  const limitOptions = topNFilter?.options?.length
    ? topNFilter.options
    : FALLBACK_LIMIT_OPTIONS;
  const limitValue = limit ?? topNFilter?.default ?? 10;

  const maxAbs = useMemo(() => {
    let m = 0;
    for (const pt of points) {
      const dev =
        measure === "median"
          ? pt.median_deviation_days
          : pt.mean_deviation_days;
      if (isNumeric(dev)) {
        const a = Math.abs(dev);
        if (a > m) m = a;
      }
    }
    return m || 1;
  }, [points, measure]);

  if (canFetch ? isFetching : isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const limitSelect = canFetch ? (
    <select
      className="form-select form-select-sm"
      style={{
        width: "auto",
        fontSize: 11,
        minWidth: 72,
        padding: "1px 24px 1px 6px",
        cursor: "pointer",
        marginLeft: "auto",
        flexShrink: 0,
      }}
      value={String(limitValue)}
      onChange={(e) => {
        const v = e.target.value;
        setLimit(v === "all" ? v : Number(v));
      }}
      aria-label="How many rows to show"
    >
      {limitOptions.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  ) : null;

  if (points.length === 0) {
    return (
      <div className="deviation-bar">
        {downloadMenu}
        <p className="text-muted small mb-0">No data available.</p>
      </div>
    );
  }

  const totalPayers = meta?.total_payers;
  const summary =
    totalPayers != null
      ? `Showing ${limitValue === "all" ? "all" : `top ${limitValue}`} of ${totalPayers.toLocaleString()} payers. Select Measure Type and Top N.`
      : "Select Measure Type and Top N.";

  return (
    <div className="deviation-bar">
      {downloadMenu}
      <div className="deviation-bar__meta">
        {summary && <span className="deviation-bar__summary">{summary}</span>}
        {meta?.fence_stage && (
          <span className="deviation-bar__stage">{meta.fence_stage}</span>
        )}
        {/* Segmented toggle: each option carries its overall value so the
            median↔mean gap (skew) is readable without switching. */}
        <div
          className="measure-toggle"
          role="group"
          aria-label="Compare deviation against"
        >
          {MEASURES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={measure === m.key ? "is-active" : ""}
              aria-pressed={measure === m.key}
              onClick={() => setMeasure(m.key)}
            >
              {m.label}
              <span className="measure-toggle__val">
                {fmtDays(overall[m.key])}
              </span>
            </button>
          ))}
        </div>
        {limitSelect}
      </div>

      <div className="deviation-bar__axis-label">
        <span>Faster</span>
        <span>
          {measureLabel} ({fmtDays(overall[measure])})
        </span>
        <span>Slower</span>
      </div>

      <div className="deviation-bar__chart">
        {points.map((pt) => {
          const deviation = deviationOf(pt);

          if (deviation === null) {
            return (
              <div key={pt.name} className="deviation-bar__bar-row">
                <span className="deviation-bar__bar-label" title={pt.name}>
                  {pt.name}
                </span>
                <span className="deviation-bar__bar-track">
                  <span className="deviation-bar__bar-center" />
                </span>
                <span
                  className="deviation-bar__bar-value"
                  style={{ color: "#94a3b8" }}
                >
                  N/A
                </span>
              </div>
            );
          }

          const pct = (Math.abs(deviation) / maxAbs) * 45;
          const isNeg = deviation < 0;
          const color = isNeg
            ? "#16a34a"
            : deviation > 0
              ? "#dc2626"
              : "#94a3b8";

          return (
            <div key={pt.name} className="deviation-bar__bar-row">
              <span className="deviation-bar__bar-label" title={pt.name}>
                {pt.name}
              </span>
              <span className="deviation-bar__bar-track">
                <span className="deviation-bar__bar-center" />
                <span
                  className="deviation-bar__bar-fill"
                  style={{
                    width: `${pct}%`,
                    background: color,
                    ...(isNeg
                      ? { right: "50%", borderRadius: "3px 0 0 3px" }
                      : { left: "50%", borderRadius: "0 3px 3px 0" }),
                  }}
                />
              </span>
              <span className="deviation-bar__bar-value" style={{ color }}>
                {deviation > 0 ? "+" : ""}
                {deviation.toFixed(1)}d
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
